import classNames from '@/utils/className';
import { ComponentPropsWithRef, ComponentPropsWithoutRef, forwardRef } from 'react';

const variants = {
  GalleryContainer: {
    isOpen: {
      true: '[&>div>div>div.image-gallery-slide-wrapper]:cursor-default',
      false: '[&>div>div>div>div>div_.image-gallery-slide>img]:h-full',
    },
  },
  GalleryImageContainer: {
    isFirst: {
      true: '',
      false: 'hidden',
    },
  },
  GalleryCaptionContainer: {
    isOpen: {
      true: 'bg-transparent [&>p]:text-mglGray50',
      false: '',
    },
  },
  GalleryNavButton: {
    right: {
      true: 'right-6',
      false: '',
    },
    left: {
      true: 'left-6',
      false: '',
    },
  },
} as const;

export const GalleryPlaceholder = ({ children, ...rest }: ComponentPropsWithoutRef<'div'>) => {
  return (
    <div
      className="mb-6 min-h-[calc(85vh-100px)] rounded-b bg-mglGray100 [@media_only_screen_and_(max-height:700px)]:min-h-[calc(65vh-64px)] [@media_only_screen_and_(max-height:750px)]:min-h-[calc(60vh-80px)] [@media_only_screen_and_(max-height:950px)_and_(max-width:767px)]:min-h-[calc(50vh-80px)]"
      {...rest}>
      {children}
    </div>
  );
};

export const GalleryContainer = forwardRef<HTMLDivElement, ComponentPropsWithRef<'div'> & { isOpen: boolean }>(({ children, isOpen, ...rest }, ref) => {
  return (
    <div
      ref={ref}
      className={classNames(
        'min-h-[70%] [&>div>div>.image-gallery-slide-wrapper>.image-gallery-bullets>div>button.active]:opacity-100 [&>div>div>.image-gallery-slide-wrapper>.image-gallery-bullets>div>button]:m-0 [&>div>div>.image-gallery-slide-wrapper>.image-gallery-bullets>div>button]:h-[6px] [&>div>div>.image-gallery-slide-wrapper>.image-gallery-bullets>div>button]:w-[30px] [&>div>div>.image-gallery-slide-wrapper>.image-gallery-bullets>div>button]:rounded [&>div>div>.image-gallery-slide-wrapper>.image-gallery-bullets>div>button]:bg-mglGray50 [&>div>div>.image-gallery-slide-wrapper>.image-gallery-bullets>div>button]:p-0 [&>div>div>.image-gallery-slide-wrapper>.image-gallery-bullets>div>button]:opacity-40 [&>div>div>.image-gallery-slide-wrapper>.image-gallery-bullets>div>button]:hover:bg-mglBlue400 [&>div>div>.image-gallery-slide-wrapper>.image-gallery-bullets>div]:flex [&>div>div>.image-gallery-slide-wrapper>.image-gallery-bullets>div]:flex-wrap [&>div>div>.image-gallery-slide-wrapper>.image-gallery-bullets>div]:items-center [&>div>div>.image-gallery-slide-wrapper>.image-gallery-bullets>div]:justify-center [&>div>div>.image-gallery-slide-wrapper>.image-gallery-bullets>div]:gap-2 [&>div>div>.image-gallery-slide-wrapper>.image-gallery-index]:bottom-3 [&>div>div>.image-gallery-slide-wrapper>.image-gallery-index]:left-3 [&>div>div>.image-gallery-slide-wrapper>.image-gallery-index]:right-[initial] [&>div>div>.image-gallery-slide-wrapper>.image-gallery-index]:top-[initial] [&>div>div>.image-gallery-slide-wrapper>.image-gallery-index]:rounded-2xl [&>div>div>.image-gallery-slide-wrapper>.image-gallery-index]:bg-mglGray50 [&>div>div>.image-gallery-slide-wrapper>.image-gallery-index]:px-4 [&>div>div>.image-gallery-slide-wrapper>.image-gallery-index]:py-2 [&>div>div>.image-gallery-slide-wrapper>.image-gallery-index]:text-xs [&>div>div>.image-gallery-slide-wrapper>.image-gallery-index]:leading-none [&>div>div>.image-gallery-slide-wrapper>.image-gallery-index]:text-mglGray900 [&>div>div>.image-gallery-slide-wrapper]:cursor-pointer [&>div]:h-full [&>div_.image-gallery-image]:aspect-video [&>div_.image-gallery-image]:bg-mglGray900 [&>div_.image-gallery-thumbnail-image]:h-[63px] [&>div_.image-gallery-thumbnail-image]:w-auto [&>div_.image-gallery-thumbnails>nav>button.active]:opacity-100 [&>div_.image-gallery-thumbnails>nav>button]:mx-[6px] [&>div_.image-gallery-thumbnails>nav>button]:my-1 [&>div_.image-gallery-thumbnails>nav>button]:aspect-[127/81] [&>div_.image-gallery-thumbnails>nav>button]:h-[63px] [&>div_.image-gallery-thumbnails>nav>button]:overflow-hidden [&>div_.image-gallery-thumbnails>nav>button]:border-0 [&>div_.image-gallery-thumbnails>nav>button]:bg-mglGray900 [&>div_.image-gallery-thumbnails>nav>button]:opacity-40 [&>div_.image-gallery-thumbnails>nav>button]:transition-all [&>div_.image-gallery-thumbnails>nav>button]:duration-300 [&>div_.image-gallery-thumbnails>nav>button]:ease-ease hover:[&>div_.image-gallery-thumbnails>nav>button]:opacity-80 [&>div_.image-gallery-thumbnails]:fixed [&>div_.image-gallery-thumbnails]:left-0 [&>div_.image-gallery-thumbnails]:top-[80%] [&>div_.image-gallery-thumbnails]:z-[4] [&>div_.image-gallery-thumbnails]:w-full [&>div_.image-gallery-thumbnails]:max-w-[705px] [&>div_.image-gallery-thumbnails]:overflow-hidden [&>div_.image-gallery-thumbnails]:rounded tablet:[&>div_.image-gallery-thumbnails]:left-[60px] desktop:[&>div_.image-gallery-thumbnails]:left-[initial] [@media_only_screen_and_(max-height:700px)]:min-h-full [@media_only_screen_and_(min-height:701px)_and_(max-height:750px)]:min-h-[90%] [@media_only_screen_and_(min-height:751px)_and_(max-height:800px)]:min-h-[80%] [@media_only_screen_and_(min-height:801px)_and_(max-height:950px)]:min-h-[initial]',
        variants.GalleryContainer.isOpen[isOpen.toString()]
      )}
      {...rest}>
      {children}
    </div>
  );
});

export const GalleryImageContainer = ({ children, isFirst, ...rest }: ComponentPropsWithoutRef<'a'> & { isFirst: boolean }) => {
  return (
    <a className={classNames('', variants.GalleryImageContainer.isFirst[isFirst.toString()])} {...rest}>
      {children}
    </a>
  );
};

export const GalleryCaptionContainer = ({ children, isOpen = undefined, ...rest }: ComponentPropsWithoutRef<'div'> & { isOpen?: boolean }) => {
  return (
    <div
      className={classNames(
        'mb-6 flex flex-col justify-center rounded-b bg-mglGray100 px-0 py-2',
        isOpen != null && variants.GalleryCaptionContainer.isOpen[isOpen.toString()]
      )}
      {...rest}>
      {children}
    </div>
  );
};

export const GalleryCaptionText = ({ children, ...rest }: ComponentPropsWithoutRef<'p'>) => {
  return (
    <p className="text-center text-sm text-mglGray900" {...rest}>
      {children}
    </p>
  );
};

export const GalleryCredits = ({ children, ...rest }: ComponentPropsWithoutRef<'p'>) => {
  return (
    <p className="text-center text-sm text-mglGray900 opacity-40" {...rest}>
      {children}
    </p>
  );
};

export const GalleryNavButton = ({
  children,
  left = undefined,
  right = undefined,
  ...rest
}: ComponentPropsWithoutRef<'button'> & { left?: boolean; right?: boolean }) => {
  return (
    <button
      className={classNames(
        'absolute top-[50%] z-[4] flex h-11 w-11 cursor-pointer items-center justify-center rounded-half border-none bg-mglBlue400 transition-all duration-300 ease-ease hover:bg-mglGray50 [&>svg]:text-2xl [&>svg]:text-mglGray50 [&>svg]:hover:text-mglBlue400',
        left != null && variants.GalleryNavButton.left[left.toString()],
        right != null && variants.GalleryNavButton.right[right.toString()]
      )}
      {...rest}>
      {children}
    </button>
  );
};

export default { GalleryPlaceholder, GalleryImageContainer, GalleryContainer, GalleryCaptionContainer, GalleryCaptionText, GalleryCredits };
